import {
	addressString,
	convertLLCAddressToCloserAddress,
} from '@core/util/address';
import {
	getPartiesTo,
	getProperty,
	getPurchaserOrBorrowerAddress,
	getStatement,
	getVendorAddress,
} from '@core/util/eregistration';
import { getServiceBranch } from '@core/util/llc';
import { centsToDollars } from '@core/util/money';
import {
	getAsRegInterestRate,
	getMortgageLenders,
	InsuranceAmountDescription,
} from '@core/util/mortgage';
import { getOrdinal } from '@core/util/number';
import { stringEncode } from '@core/util/string';
import { Address } from '@models/address';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClientWithAssociations,
	Capacity,
	ERegChargeType,
	MatterAdjustmentSalePriceCalculated,
	MatterBusinessUser,
	MatterChargeInstrument,
	MatterClientType,
	MatterERegTab,
	MatterFileType,
	MatterInstrumentPartiesTo,
	MatterInstrumentType,
	MatterTitle,
	MatterTransferInstrument,
	NewMatterChargeInstrument,
	NewMatterTransferInstrument,
	PersonMatterClientWithAssociations,
	UserTypeCapacity,
} from '@models/matter';
import {
	InterestRateType,
	MatterMortgage,
	MatterMortgageType,
	MatterMortgageWithAssociations,
	MortgagePercentageOfInterestTypes,
} from '@models/mortgage';
import {
	FinancialInstitutionLender,
	LenderWithAssociations,
	MortgageeType,
} from '@models/lender';
import { Selector } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { LLCMortgageState } from '@state/mortgages/llc-mortgage/llc-mortgage.state';
import { capitalize, cloneDeep } from 'lodash';
import { MatterClients } from './matter_clients';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import {
	clientDirectFormattedName,
	clientFormattedName,
} from '@core/util/client';
import { validateAndFormatFNSDate } from '@core/util/datetime';
import { BusinessAddressesState } from '@state/business/address';
import { LawFirm } from '@models/lawFirms';
import { isSet } from '@core/util/value';
import { BusinessAddress } from '@models/businesses';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { RepresentationType } from '@models/clients';

export class EregXmlSelectors {
	@Selector([
		MatterMortgagesState.getAllMortgagesWithAssociations,
		SelectedMatter.chargeDetails,
		SelectedMatter.matterFileType,
		MatterClients.get_allMatterClientsWithAssociationMapped,
		MatterTitlesState.getAll,
		SelectedMatter.interestEstate,
		SelectedMatter.matterNumber,
		LLCMortgageState.getLLCMortgages,
		SelectedMatter.address,
		MatterMortgagesState.getVTBMortgage,
	])
	static getChargeXml(
		mortgages: MatterMortgageWithAssociations[],
		chargeDetails: ERegChargeType,
		matterFileType: MatterFileType,
		allClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		titles: MatterTitle[],
		interestEstate: string,
		matterNumber: string,
		llcMortgages: LLCTransaction[],
		address: Address,
		vtbMortgage: MatterMortgage,
	): (mortgageId: string) => Partial<MatterChargeInstrument> {
		return (mortgageId: string) => {
			const chargeObject = cloneDeep(NewMatterChargeInstrument);

			const mortgage = mortgages.find(
				(_mortgage) => _mortgage.id === mortgageId,
			);

			if (mortgage) {
				const lenders = getMortgageLenders(
					mortgage,
				) as LenderWithAssociations[];

				let llcMortgage: LLCTransaction;

				if (mortgage.type === MatterMortgageType.LLC) {
					llcMortgage = llcMortgages.find(
						(tmpLlcMortgage) =>
							tmpLlcMortgage.id === mortgage.relatedMortgageId,
					);
				}

				const guarantors = mortgage.guarantors?.map((guarantorId) =>
					allClients.find((client) => client.id === guarantorId),
				);

				const selectedChargeDetails = chargeDetails?.[mortgageId];

				const clients = allClients.filter((x) => {
					if (matterFileType === MatterFileType.PURCHASE) {
						return x?.clientTypes?.includes(MatterClientType.BUYER);
					}

					if (matterFileType === MatterFileType.REFINANCE) {
						return x?.clientTypes?.includes(MatterClientType.BORROWER);
					}

					if (matterFileType === MatterFileType.SALE && vtbMortgage) {
						return x?.clientTypes?.includes(MatterClientType.BUYER);
					}

					return false;
				});

				const partiesFromAddress = getPurchaserOrBorrowerAddress(
					clients,
					address,
				);

				const statement = getStatement(clients);

				chargeObject.NumberOfInstruments = 1;

				const suffix = ` ${getOrdinal(mortgage?.priority)} CHARGE`;

				chargeObject.Instrument.Name = isSet(
					selectedChargeDetails?.documentName,
				)
					? `${selectedChargeDetails.documentName.substring(0, 35 - suffix.length)}${suffix}`
					: `${matterNumber?.substring(0, 35 - suffix.length)}${suffix}`;

				chargeObject.Instrument.AdditionalTerms = mortgage?.additionClauses;

				chargeObject.Instrument.Amount = Number(
					centsToDollars(mortgage?.amount),
				);
				chargeObject.Instrument.AmountOfPayment = mortgage?.regularPaymentsAsReg
					? mortgage?.regularPaymentsAsReg.replace(/[,\\$]/g, '')
					: centsToDollars(mortgage?.regularPayments);

				chargeObject.Instrument.BalanceDueDate = mortgage?.maturityDateAsReg
					? (validateAndFormatFNSDate(mortgage?.maturityDateAsReg) ??
						mortgage?.maturityDateAsReg)
					: validateAndFormatFNSDate(mortgage?.maturityDate);

				chargeObject.Instrument.CalculationPeriod =
					mortgage?.interestCalculatedAsReg ?? mortgage?.interestCalculated;

				chargeObject.Instrument.Currency = 'CDN';

				chargeObject.Instrument.FirstPaymentDate =
					validateAndFormatFNSDate(mortgage?.firstPaymentDateAsReg) ??
					validateAndFormatFNSDate(mortgage?.firstPaymentDate);

				chargeObject.Instrument.GuarantorName = guarantors
					?.map((g) => clientDirectFormattedName(g))
					?.join('; ');

				chargeObject.Instrument.Insurance = mortgage?.insuranceAmount
					? capitalize(
							InsuranceAmountDescription?.[
								mortgage?.insuranceAmount
							]?.toLowerCase(),
						)
					: InsuranceAmountDescription.full;

				chargeObject.Instrument.InterestAdjustmentDate =
					validateAndFormatFNSDate(mortgage?.interestAdjustmentDateAsReg) ??
					validateAndFormatFNSDate(mortgage?.interestAdjustmentDate);

				chargeObject.Instrument.InterestRate =
					EregXmlSelectors.getInterestRate(mortgage);

				chargeObject.Instrument.LastPaymentDate =
					validateAndFormatFNSDate(mortgage?.lastPaymentDateAsReg) ??
					validateAndFormatFNSDate(mortgage?.lastPaymentDate);

				chargeObject.Instrument.PaymentDate =
					mortgage?.paymentDueAsReg ?? mortgage?.paymentDue;

				chargeObject.Instrument.StdChargeTermsFileNr =
					mortgage?.standardChargeTerm;

				chargeObject.Instrument.PartiesFrom.AddressForService =
					partiesFromAddress
						? stringEncode(addressString(partiesFromAddress))
						: '';
				if (statement) {
					chargeObject.Instrument.PartiesFrom.Statement = statement;
				}
				chargeObject.Instrument.PartiesTo = lenders.map((x) => {
					// todo sc-15710: remove override workflow
					const idsWithOverrides = [
						'hiDUrSC60oSsg8trAn2b',
						'X7E6wp7WNl42WnjqM3TT',
					];
					let instance;
					const hasOverrides = idsWithOverrides.includes(x.id);
					if (hasOverrides) {
						instance = this.applyEregOverrides(x);
					} else {
						instance = x;
					}

					let fullName =
						instance?.name || clientFormattedName(instance?.client);

					if (mortgage?.type === MatterMortgageType.LLC) {
						fullName =
							llcMortgage?.mortgagee?.nameEn ?? llcMortgage.lender?.nameEn;
					} else if (mortgage?.type === MatterMortgageType.CLOSER_SYNC) {
						fullName = instance?.mortgageeName ?? instance?.name;
					}

					const result: Partial<MatterInstrumentPartiesTo> = {
						FullName: fullName,
					};

					if (
						mortgage.percentageOfInterestType ===
							MortgagePercentageOfInterestTypes.CAPACITY_PER_CLIENT &&
						instance.percentageOfInterest
					) {
						result.Share = instance.percentageOfInterest;
					}

					// here
					result.AddressForService =
						instance?.mortgageeType === MortgageeType.PRIVATE_LENDER
							? addressString(
									instance?.client?.serviceAddress ||
										instance?.client?.reportAddress ||
										address,
								)
							: EregXmlSelectors.getInstitutionAddressForService(
									mortgage.type,
									instance,
									llcMortgage,
								);

					// todo sc-15710: remove override workflow
					if (hasOverrides) {
						result.AddressForService = this.serviceAddressOverrides(x);
					}

					if (
						mortgage.percentageOfInterestType ===
							MortgagePercentageOfInterestTypes.CAPACITY_PER_CLIENT &&
						instance.percentageOfInterestType
					) {
						result.UserCapacity =
							UserTypeCapacity[instance.percentageOfInterestType];
					}
					return result as MatterInstrumentPartiesTo;
				});

				chargeObject.Instrument.Properties = titles.length
					? titles.map((x) => getProperty(x, titles, interestEstate))
					: null;

				chargeObject.Instrument.ClientFileNumber.PartyIndicator = 'F';
				chargeObject.Instrument.ClientFileNumber.ReferenceNumber = matterNumber;
			}

			return this.removeEmpty(chargeObject) as MatterChargeInstrument;
		};
	}

	@Selector([
		SelectedMatter.transferDetails,
		SelectedMatter.matterName,
		SelectedMatter.matterNumber,
		SelectedMatter.matterFileType,
		SelectedMatter.address,
		SelectedMatter.capacity,
		SelectedMatter.interestEstate,
		MatterTitlesState.getAll,
		MatterClients.get_allMatterClientsWithAssociationMapped,
		SelectedMatter.assignedLawyer,
		SelectedMatter.otherPartyLawFirm,
		BusinessAddressesState.getPrimaryAddress,
		AdjustmentsSelector.salePriceAdjustmentCalculated,
		EregXmlSelectors.isTPR,
	])
	static getTransferXml(
		transferDetails: MatterERegTab,
		matterName: string,
		matterNumber: string,
		matterFileType: MatterFileType,
		address: Address,
		capacity: Capacity,
		interestEstate: string,
		titles: MatterTitle[],
		allClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		assignedLawyer: MatterBusinessUser,
		otherPartyLawFirm: LawFirm,
		businessAddress: BusinessAddress,
		salePriceCalc: MatterAdjustmentSalePriceCalculated,
		isTPR: boolean,
	): MatterTransferInstrument {
		const transferObject = cloneDeep(NewMatterTransferInstrument);

		transferObject.NumberOfInstruments = 1;

		transferObject.Instrument.Name = isSet(transferDetails?.documentName)
			? transferDetails?.documentName?.substring(0, 35)?.toUpperCase()
			: matterName?.substring(0, 35)?.toUpperCase();

		const sellers = allClients.filter((x) =>
			x?.clientTypes?.includes(MatterClientType.SELLER),
		);
		const offerors = allClients.filter((x) =>
			x?.clientTypes?.includes(MatterClientType.OFFEROR),
		);

		if (isTPR) {
			transferObject.InstrumentTypeId =
				MatterInstrumentType.TRANSFER_VIA_PERSONAL_REPRESENTATIVE;
		}

		const vendorAddress = getVendorAddress(
			sellers,
			matterFileType,
			assignedLawyer,
			otherPartyLawFirm,
			businessAddress,
		);
		const statement = getStatement(sellers);

		const partiesTo = getPartiesTo(offerors, address, capacity) ?? [];

		if (vendorAddress) {
			transferObject.Instrument.PartiesFrom.AddressForService = stringEncode(
				addressString(vendorAddress),
			);
		}

		if (statement) {
			transferObject.Instrument.PartiesFrom.Statement = statement;
		}

		transferObject.Instrument.PartiesTo = partiesTo;
		transferObject.Instrument.Properties = titles
			? Object.values(titles).map((x) => getProperty(x, titles, interestEstate))
			: null;
		transferObject.Instrument.ActingFor =
			matterFileType === MatterFileType.SALE ? 'F' : 'T';
		transferObject.Instrument.FeeResponsibilityIndicator = 'T';
		transferObject.Instrument.Amount = Number(
			centsToDollars(salePriceCalc.nsp),
		);
		transferObject.Instrument.CashAmount = Number(
			centsToDollars(salePriceCalc.nsp),
		);
		transferObject.Instrument.ClientFileNumber.PartyIndicator =
			matterFileType === MatterFileType.SALE ? 'F' : 'T';
		transferObject.Instrument.ClientFileNumber.ReferenceNumber = matterNumber;

		return this.removeEmpty(transferObject) as MatterTransferInstrument;
	}

	@Selector([MatterClients.get_allMatterClientsWithAssociationMapped])
	static isTPR(
		allClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): boolean {
		return allClients.some(
			(x) =>
				x?.clientTypes?.includes(MatterClientType.SELLER) &&
				(x as PersonMatterClientWithAssociations)?.representation ===
					RepresentationType.ESTATE,
		);
	}

	static getInterestRate = (mortgage: MatterMortgage): string => {
		if (mortgage?.interestRateAsReg) {
			if (!isNaN(Number(mortgage?.interestRateAsReg))) {
				if (mortgage?.interestRateType === InterestRateType.FIXED) {
					return `${mortgage?.interestRateAsReg}% per annum`;
				}

				if (Number(mortgage?.interestRateAsReg) < 0) {
					return `prime ${mortgage?.interestRateAsReg}%`;
				}

				return `prime +${mortgage?.interestRateAsReg}%`;
			}

			return mortgage?.interestRateAsReg;
		}

		return getAsRegInterestRate(mortgage);
	};

	static getInstitutionAddressForService = (
		mortgageType: MatterMortgageType,
		financialInstitution: FinancialInstitutionLender,
		llcMortgage: LLCTransaction,
	): string => {
		if (mortgageType === MatterMortgageType.LLC) {
			const branch = getServiceBranch(llcMortgage.data);
			const address = convertLLCAddressToCloserAddress(branch.address);

			return addressString(address);
		}

		if (
			financialInstitution?.branch?.institutionAddressForService ||
			!financialInstitution?.branch
		) {
			return addressString(financialInstitution?.address);
		}

		return addressString(financialInstitution?.branch?.address);
	};

	static removeEmpty(obj) {
		const finalObj = {};
		Object.keys(obj).forEach((key) => {
			if (
				obj[key] &&
				typeof obj[key] === 'object' &&
				!Array.isArray(obj[key])
			) {
				const nestedObj = this.removeEmpty(obj[key]);
				if (Object.keys(nestedObj).length) {
					finalObj[key] = nestedObj;
				}
			} else if (
				obj[key] !== '' &&
				obj[key] !== undefined &&
				obj[key] !== null
			) {
				finalObj[key] = obj[key];
			}
		});

		return finalObj;
	}

	// todo sc-15710: remove override workflow
	private static applyEregOverrides(lender: LenderWithAssociations) {
		// mcap
		// mortgagee to e-reg as "Computershare Trust Company of Canada"
		// mortgagee to title insurance as "Computershare Trust Company of Canada"
		const nameOverride = 'Computershare Trust Company of Canada';
		if (
			'hiDUrSC60oSsg8trAn2b' === lender.id ||
			'X7E6wp7WNl42WnjqM3TT' === lender.id
		) {
			return {
				...lender,
				name: nameOverride,
			};
		}

		return lender;
	}

	// todo sc-15710: remove override workflow
	private static serviceAddressOverrides(
		lender: LenderWithAssociations,
	): string {
		// mcap
		// service address "c/o MCAP Service Corporation, P.O. Box 351, Station C, Kitchener, ON, N2G 3Y9"
		if ('hiDUrSC60oSsg8trAn2b' === lender.id) {
			return 'c/o MCAP Service Corporation, P.O. Box 351, Station C, Kitchener, ON, N2G 3Y9';
		}

		// fnf
		// service address: c/o First National Financial LP, Suite 1900-16 York Street, Toronto, ON, M5J 0E6
		if ('X7E6wp7WNl42WnjqM3TT' === lender.id) {
			return 'c/o First National Financial LP, Suite 1900-16 York Street, Toronto, ON, M5J 0E6';
		}
	}
}
